import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSpinner, FaExclamationCircle, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { chargeService } from '../../../services/chargeService';
import { fetchAllProducts } from '../controllers/products-controller';
import { fetchPlatforms } from '../platform/controller/platform-controller';

const ChargesPage = () => {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [deletingCharge, setDeletingCharge] = useState(null);

  const [allProducts, setAllProducts] = useState([]);
  const [allPlateforms, setAllPlateforms] = useState([]);
  const [formData, setFormData] = useState({ typeCharge: '', date: '', values: '', productId: '', plateformId: '' });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const productDropdownRef = useRef(null);

  // Close product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target)) {
        setProductDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.title = 'Charges | Admin';
    fetchAllProducts()
      .then(res => setAllProducts(Array.isArray(res) ? res : []))
      .catch(() => setAllProducts([]));
    fetchPlatforms(1, 1000)
      .then(res => {
        const list = res?.list || res?.data || res || [];
        setAllPlateforms(Array.isArray(list) ? list : []);
      })
      .catch(() => setAllPlateforms([]));
  }, []);

  const fetchCharges = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await chargeService.getCharges(currentPage, pageSize, searchTerm);
      setCharges(response.data || []);
      setTotalItems(response.totalItems || 0);
      setTotalPages(response.totalPages || 0);
    } catch {
      setError('Erreur lors du chargement des charges');
      setCharges([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => fetchCharges(), 400);
    return () => clearTimeout(t);
  }, [fetchCharges]);

  const openCreateModal = () => {
    setEditingCharge(null);
    setFormData({ typeCharge: '', date: '', values: '', productId: '', plateformId: '' });
    setProductSearch('');
    setProductDropdownOpen(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (charge) => {
    setEditingCharge(charge);
    setFormData({
      typeCharge: charge.typeCharge || '',
      date: charge.date ? charge.date.substring(0, 10) : '',
      values: charge.values || '',
      productId: charge.productId || '',
      plateformId: charge.plateformId || '',
    });
    setProductSearch('');
    setProductDropdownOpen(false);
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    try {
      if (editingCharge) {
        await chargeService.updateCharge(editingCharge.id, formData);
        setSuccessMessage('Charge mise à jour avec succès');
      } else {
        await chargeService.createCharge(formData);
        setSuccessMessage('Charge créée avec succès');
      }
      setIsFormModalOpen(false);
      setEditingCharge(null);
      fetchCharges();
    } catch {
      setError(editingCharge ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await chargeService.deleteCharge(deletingCharge.id);
      setSuccessMessage('Charge supprimée avec succès');
      setIsDeleteModalOpen(false);
      setDeletingCharge(null);
      fetchCharges();
    } catch {
      setError('Erreur lors de la suppression');
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const chargesArray = Array.isArray(charges) ? charges : [];

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Charges</h1>
          <p className="mt-1 text-sm text-gray-500">Gérez vos charges et dépenses opérationnelles</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Rechercher..."
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-emerald-500 focus:border-emerald-500 w-48"
            />
          </div>
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
          >
            <FaPlus className="-ml-1 mr-2 h-4 w-4" />
            Ajouter une charge
          </button>
        </div>
      </div>

      {/* Alert messages */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <FaExclamationCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto -mx-1.5 -my-1.5 inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100">
              <FaPlus className="h-4 w-4 transform rotate-45" />
            </button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 rounded-md bg-green-50 p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm font-medium text-green-800">{successMessage}</p>
            <button onClick={() => setSuccessMessage(null)} className="ml-auto -mx-1.5 -my-1.5 inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100">
              <FaPlus className="h-4 w-4 transform rotate-45" />
            </button>
          </div>
        </div>
      )}

      {/* Charges list */}
      <div className="mt-4 overflow-hidden bg-white shadow sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {loading && chargesArray.length === 0 ? (
            <li className="flex justify-center items-center p-12">
              <FaSpinner className="h-8 w-8 text-emerald-500 animate-spin" />
            </li>
          ) : chargesArray.length === 0 ? (
            <li className="flex flex-col items-center justify-center p-12 text-center">
              <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune charge</h3>
              <p className="mt-1 text-sm text-gray-500">Commencez par créer une nouvelle charge.</p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="inline-flex items-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700"
                >
                  <FaPlus className="-ml-1 mr-2 h-5 w-5" />
                  Ajouter une charge
                </button>
              </div>
            </li>
          ) : (
            chargesArray.map((charge) => (
              <li key={charge.id}>
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-emerald-100 rounded-full">
                      <span className="text-sm font-bold text-emerald-800">
                        {charge.typeCharge ? charge.typeCharge.charAt(0).toUpperCase() : '?'}
                      </span>
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-medium text-gray-900">{charge.typeCharge}</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          {Number(charge.values).toFixed(2)} TND
                        </span>
                        {charge.product && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {charge.product.reference}
                          </span>
                        )}
                        {charge.plateform && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {charge.plateform.name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">{formatDate(charge.date)}</div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex space-x-2 ml-4">
                    <button
                      type="button"
                      onClick={() => openEditModal(charge)}
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                    >
                      <FaEdit className="h-4 w-4 text-gray-500" />
                      <span className="ml-2 hidden sm:inline">Modifier</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setDeletingCharge(charge); setIsDeleteModalOpen(true); }}
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      <FaTrash className="h-4 w-4 text-red-500" />
                      <span className="ml-2 hidden sm:inline">Supprimer</span>
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>

        {/* Pagination */}
        {chargesArray.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage <= 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                Précédent
              </button>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage >= totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700">
                {totalItems > 0 ? (<>Affichage de <span className="font-medium">{Math.min((currentPage - 1) * pageSize + 1, totalItems)}</span> à <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span> sur <span className="font-medium">{totalItems}</span> charges</>) : 'Aucun résultat'}
              </p>
              <div className="flex items-center gap-3">
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} className="border border-gray-300 rounded-md text-sm py-1 pl-2 pr-7 focus:ring-emerald-500 focus:border-emerald-500">
                  {[5, 10, 25, 50].map(s => <option key={s} value={s}>{s} / page</option>)}
                </select>
              {totalPages > 1 && (
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${currentPage <= 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                    <FaChevronLeft className="h-4 w-4" />
                  </button>
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    return (
                      <button key={page} onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page ? 'z-10 bg-emerald-50 border-emerald-500 text-emerald-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                        {page}
                      </button>
                    );
                  })}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${currentPage >= totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                    <FaChevronRight className="h-4 w-4" />
                  </button>
                </nav>
              )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsFormModalOpen(false)} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-5">
                {editingCharge ? 'Modifier la charge' : 'Nouvelle charge'}
              </h3>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de charge *</label>
                  <input
                    type="text"
                    required
                    value={formData.typeCharge}
                    onChange={e => setFormData({ ...formData, typeCharge: e.target.value })}
                    placeholder="Ex: Publicité, Livraison, Emballage..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant (TND) *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    value={formData.values}
                    onChange={e => setFormData({ ...formData, values: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plateforme *</label>
                  <select
                    required
                    value={formData.plateformId}
                    onChange={e => setFormData({ ...formData, plateformId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white"
                  >
                    <option value="">-- Sélectionner une plateforme --</option>
                    {allPlateforms.map(pf => (
                      <option key={pf.id} value={pf.id}>{pf.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Produit associé (optionnel)</label>
                  <div className="relative" ref={productDropdownRef}>
                    {formData.productId ? (
                      <div className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm">
                        <span className="truncate">
                          {(() => {
                            const p = allProducts.find(p => String(p.id) === String(formData.productId));
                            return p ? `${p.reference} - ${p.name}` : 'Produit sélectionné';
                          })()}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setFormData({ ...formData, productId: '' }); setProductSearch(''); }}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          <FaTimes className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={productSearch}
                        onChange={e => { setProductSearch(e.target.value); setProductDropdownOpen(true); }}
                        onFocus={() => setProductDropdownOpen(true)}
                        placeholder="Rechercher un produit..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      />
                    )}
                    {productDropdownOpen && !formData.productId && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        <li
                          className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 cursor-pointer"
                          onClick={() => { setFormData({ ...formData, productId: '' }); setProductSearch(''); setProductDropdownOpen(false); }}
                        >
                          Aucun produit
                        </li>
                        {allProducts
                          .filter(p => {
                            const q = productSearch.toLowerCase();
                            return !q || (p.reference && p.reference.toLowerCase().includes(q)) || (p.name && p.name.toLowerCase().includes(q));
                          })
                          .map(p => (
                            <li
                              key={p.id}
                              className="px-3 py-2 text-sm text-gray-900 hover:bg-emerald-50 cursor-pointer"
                              onClick={() => { setFormData({ ...formData, productId: p.id }); setProductSearch(''); setProductDropdownOpen(false); }}
                            >
                              <span className="font-medium">{p.reference}</span> - {p.name}
                            </li>
                          ))
                        }
                        {allProducts.filter(p => {
                          const q = productSearch.toLowerCase();
                          return !q || (p.reference && p.reference.toLowerCase().includes(q)) || (p.name && p.name.toLowerCase().includes(q));
                        }).length === 0 && (
                          <li className="px-3 py-2 text-sm text-gray-400 italic">Aucun produit trouvé</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsFormModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
                  >
                    {formSubmitting && <FaSpinner className="animate-spin mr-2 h-4 w-4" />}
                    {editingCharge ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsDeleteModalOpen(false)} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <FaExclamationCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Supprimer la charge</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Voulez-vous vraiment supprimer <strong>"{deletingCharge?.typeCharge}"</strong> du {deletingCharge ? new Date(deletingCharge.date).toLocaleDateString('fr-FR') : ''} ?
                      Cette action est irréversible.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-60"
                >
                  {loading ? <FaSpinner className="animate-spin h-4 w-4" /> : 'Supprimer'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChargesPage;
